/**
 * @Author: Zero
 * @Date: 2026-06-23 14:32:07
 * @Explain: 全局监听 Google reCAPTCHA 次级资源加载失败&网络断开事件的自定义 Hook
 */
import { useEffect, useRef, useState } from 'react';

interface MonitorOptions {
  // 当检测到谷歌资源加载失败时的回调函数
  onError?: (failedUrl: string) => void;
}

export const useRecaptchaResourceMonitor = ({
  onError,
}: MonitorOptions = {}) => {
  // 资源加载错误状态
  const [resourceError, setResourceError] = useState(false);
  // 加载失败的资源 URL
  const [failedUrl, setFailedUrl] = useState<string>('');
  // 网络错误状态
  const [networkError, setNetworkError] = useState(false);
  // 使用 useRef 保存最新的回调函数，避免 useEffect 频繁重新绑定事件
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const handleResourceError = (event: Event) => {
      const target = event.target as HTMLElement | null;

      // 确保是资源加载错误（Script 或 Link 标签），而不是普通 JS 运行错误
      if (
        target &&
        (target.tagName === 'SCRIPT' || target.tagName === 'LINK')
      ) {
        const url =
          (target as HTMLScriptElement).src || (target as HTMLLinkElement).href;

        // 过滤出谷歌验证次级资源加载失败的资源域名
        if (
          url &&
          (url.includes('gstatic.com') || url.includes('google.com/recaptcha'))
        ) {
          setResourceError(true);
          setFailedUrl(url);

          // 触发外部传入的回调
          if (onErrorRef.current) {
            onErrorRef.current(url);
          }
        }
      }
    };

    // 监听网络断开事件
    const handleOffline = () => setNetworkError(true);

    // 核心关键：第三个参数必须为 true，在【捕获阶段】监听
    // 因为 script/img 的 error 事件不冒泡，只能在捕获阶段拿到
    window.addEventListener('error', handleResourceError, true);
    // 监听网络连接事件
    window.addEventListener('offline', handleOffline);
    return () => {
      // 组件卸载时移除监听
      window.removeEventListener('error', handleResourceError, true);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { networkError, resourceError, failedUrl };
};
